import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  useTheme,
  Stack,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Import available images from src/assets
import analyticsImage from '../../assets/analytics.png';
import analyticsHistoryImage from '../../assets/analytics-history.png';
import userDashboardImage from '../../assets/user-dashboard.png';
import mobileLoginImage from '../../assets/mobile-login.jpg';
import mobileCardImage from '../../assets/mobile-card.jpg';
import mobileShareImage from '../../assets/mobile-share.jpg';
import loginLight from '../../assets/login-l.png';
import loginDark from '../../assets/login-d.png';
import dashboardLight from '../../assets/user-dashboard-l.png';
import dashboardDark from '../../assets/user-dashboard.png';

// Import wave image
import waveImage from '../../assets/cardwave.png';

// Animation keyframes
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-15px) rotate(1deg); }
  75% { transform: translateY(15px) rotate(-1deg); }
`;

const shine = keyframes`
  0% { background-position: -100% 100%; }
  100% { background-position: 100% 100%; }
`;

const LandingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.scroll-reveal');
      elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight - 100;
        if (isVisible) {
          el.classList.add('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0A1930 0%, #1A365D 100%)',
      overflow: 'hidden',
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 12 }, position: 'relative' }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  color: 'white',
                  mb: 3,
                  background: 'linear-gradient(to right, #fff, #4F46E5, #fff)',
                  backgroundSize: '200% 100%',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  animation: `${shine} 3s infinite linear`,
                }}
              >
                Digital Cards Reimagined
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                Create stunning digital business cards with powerful analytics and seamless sharing
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  background: 'linear-gradient(45deg, #4F46E5, #7C3AED)',
                  px: 4,
                  py: 2,
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #7C3AED, #4F46E5)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Get Started
              </Button>
            </motion.div>
          </Grid>
          
          {/* Mobile Preview - Floating Phone */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              position: 'relative',
              height: { xs: '500px', md: '600px' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                style={{
                  position: 'relative',
                  width: '280px',
                  height: '500px',
                  animation: `${float} 6s ease-in-out infinite`
                }}
              >
                {/* Phone Frame */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-12px',
                    left: '-12px',
                    right: '-12px',
                    bottom: '-12px',
                    borderRadius: '45px',
                    border: '12px solid #2A2A2A',
                    background: '#000',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '0px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '150px',
                      height: '24px',
                      background: '#2A2A2A',
                      borderBottomLeftRadius: '20px',
                      borderBottomRightRadius: '20px',
                      zIndex: 2
                    }
                  }}
                />

                {/* Content */}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #1A365D 0%, #0A1930 100%)',
                    borderRadius: '35px',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '30px 20px',
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      mb: 3,
                      mt: 4
                    }}
                  >
                    Digital Business Card
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      mb: 3,
                      lineHeight: 1.8
                    }}
                  >
                    John Smith<br/>
                    Software Developer<br/>
                    Powersoft Technologies
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      mb: 3,
                      lineHeight: 1.8
                    }}
                  >
                    Contact:<br/>
                    john@example.com<br/>
                    +1 234 567 8900
                  </Typography>

                  <Box
                    sx={{
                      mt: 'auto',
                      pt: 3,
                      borderTop: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.6)',
                      }}
                    >
                      Tap to view full profile
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box className="scroll-reveal" sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 700,
              color: 'white',
              mb: 2,
            }}
          >
            Powerful Features
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Everything you need to create and manage your digital presence
          </Typography>
        </Box>

        {/* Analytics Section */}
        <Grid container spacing={6} alignItems="center" sx={{ mb: 12 }}>
          <Grid item xs={12} md={6}>
            <Box className="scroll-reveal" sx={{ position: 'relative' }}>
              <motion.img
                src={analyticsImage}
                alt="Analytics Dashboard"
                style={{
                  width: '100%',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className="scroll-reveal" sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ color: 'white', mb: 3 }}>
                Advanced Analytics
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4 }}>
                Track your card's performance with detailed analytics. Monitor views, interactions, and engagement in real-time.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Dashboard Section */}
        <Grid container spacing={6} sx={{ mb: 12 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box className="scroll-reveal" sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ color: 'white', mb: 3 }}>
                Powerful Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4 }}>
                Manage all your digital cards from a centralized dashboard. Create, edit, and organize your cards with ease.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className="scroll-reveal" sx={{ position: 'relative' }}>
              <motion.img
                src={userDashboardImage}
                alt="User Dashboard"
                style={{
                  width: '100%',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Analytics History Section */}
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box className="scroll-reveal" sx={{ position: 'relative' }}>
              <motion.img
                src={analyticsHistoryImage}
                alt="Analytics History"
                style={{
                  width: '100%',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className="scroll-reveal" sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ color: 'white', mb: 3 }}>
                Analytics History
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4 }}>
                View detailed historical data of your card's performance. Track trends, analyze patterns, and make data-driven decisions.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Experience Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
        py: { xs: 8, md: 12 },
        mt: 8
      }}>
        <Container maxWidth="lg">
          <Box className="scroll-reveal" sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                color: 'white',
                mb: 2,
              }}
            >
              Seamless Mobile Experience
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              Access and manage your digital cards anywhere, anytime
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: '500px', md: '600px' },
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      transition: 'transform 0.3s ease-in-out',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={mobileLoginImage}
                    alt="Mobile Login"
                    sx={{
                      height: '100%',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: '20px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      mt: 2,
                      textAlign: 'center',
                      position: 'absolute',
                      bottom: '-40px',
                      width: '100%',
                    }}
                  >
                    Quick Login
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: '500px', md: '600px' },
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      transition: 'transform 0.3s ease-in-out',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={mobileCardImage}
                    alt="Mobile Card View"
                    sx={{
                      height: '100%',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: '20px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      mt: 2,
                      textAlign: 'center',
                      position: 'absolute',
                      bottom: '-40px',
                      width: '100%',
                    }}
                  >
                    Digital Card
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: '500px', md: '600px' },
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      transition: 'transform 0.3s ease-in-out',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={mobileShareImage}
                    alt="Mobile Sharing"
                    sx={{
                      height: '100%',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: '20px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      mt: 2,
                      textAlign: 'center',
                      position: 'absolute',
                      bottom: '-40px',
                      width: '100%',
                    }}
                  >
                    Easy Sharing
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          <Box className="scroll-reveal" sx={{ textAlign: 'center', mt: 8 }}>
            <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)', mb: 4 }}>
              Experience the future of networking with our mobile-first digital cards
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Theme Modes Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box className="scroll-reveal" sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 700,
              color: 'white',
              mb: 2,
            }}
          >
            Choose Your Theme
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Switch seamlessly between light and dark modes for comfortable viewing
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 12 }}>
          {/* Login Themes */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', height: '500px', perspective: '1000px' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* Dark Theme Image */}
                <Box
                  component="img"
                  src={loginDark}
                  alt="Login Dark Theme"
                  sx={{
                    position: 'absolute',
                    width: '90%',
                    right: '0%',
                    top: '0%',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    transition: 'transform 0.5s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-20px) translateX(20px)',
                    },
                    zIndex: 2,
                  }}
                />
                {/* Light Theme Image */}
                <Box
                  component="img"
                  src={loginLight}
                  alt="Login Light Theme"
                  sx={{
                    position: 'absolute',
                    width: '90%',
                    left: '0%',
                    bottom: '0%',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    transition: 'transform 0.5s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(20px) translateX(-20px)',
                    },
                    zIndex: 1,
                  }}
                />
              </motion.div>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  textAlign: 'center',
                  position: 'absolute',
                  bottom: '-60px',
                  width: '100%',
                }}
              >
                Login Experience
              </Typography>
            </Box>
          </Grid>

          {/* Dashboard Themes */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', height: '500px', perspective: '1000px' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* Dark Theme Image */}
                <Box
                  component="img"
                  src={dashboardDark}
                  alt="Dashboard Dark Theme"
                  sx={{
                    position: 'absolute',
                    width: '90%',
                    right: '0%',
                    top: '0%',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    transition: 'transform 0.5s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-20px) translateX(20px)',
                    },
                    zIndex: 2,
                  }}
                />
                {/* Light Theme Image */}
                <Box
                  component="img"
                  src={dashboardLight}
                  alt="Dashboard Light Theme"
                  sx={{
                    position: 'absolute',
                    width: '90%',
                    left: '0%',
                    bottom: '0%',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    transition: 'transform 0.5s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(20px) translateX(-20px)',
                    },
                    zIndex: 1,
                  }}
                />
              </motion.div>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  textAlign: 'center',
                  position: 'absolute',
                  bottom: '-60px',
                  width: '100%',
                }}
              >
                Dashboard Experience
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box className="scroll-reveal" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)', mb: 4 }}>
            Customize your experience with our thoughtfully designed themes
          </Typography>
        </Box>
      </Container>

      {/* Card Themes Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box className="scroll-reveal" sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 700,
              color: 'white',
              mb: 2,
            }}
          >
            Card Themes
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Choose from our professionally designed themes for your digital card
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {[
            {
              name: 'Modern Theme',
              description: 'Clean and contemporary design with bold typography',
              gradient: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)'
            },
            {
              name: 'Corporate Theme',
              description: 'Professional and sophisticated styling for business',
              gradient: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)'
            },
            {
              name: 'Minimalist Theme',
              description: 'Simple and elegant with focus on essential information',
              gradient: 'linear-gradient(135deg, #374151 0%, #6B7280 100%)'
            },
            {
              name: 'Startup Theme',
              description: 'Dynamic and energetic design for modern businesses',
              gradient: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)'
            }
          ].map((theme, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box
                  sx={{
                    background: theme.gradient,
                    borderRadius: '20px',
                    p: 4,
                    height: '100%',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease-in-out',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                      '& .theme-overlay': {
                        opacity: 1,
                      },
                      '& .theme-content': {
                        transform: 'translateY(0)',
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 100%)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }
                  }}
                >
                  <Box
                    className="theme-content"
                    sx={{
                      position: 'relative',
                      transition: 'transform 0.3s ease-in-out',
                      transform: 'translateY(20px)',
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        mb: 2,
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      {theme.name}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '0.9rem',
                        opacity: 0.9,
                      }}
                    >
                      {theme.description}
                    </Typography>
                  </Box>
                  <Box
                    className="theme-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0,0,0,0.1)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box
          className="scroll-reveal"
          sx={{
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
            borderRadius: '24px',
            p: { xs: 4, md: 8 },
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Typography variant="h3" sx={{ color: 'white', mb: 3 }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4 }}>
            Join thousands of professionals who've already made the switch
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{
              background: 'linear-gradient(45deg, #4F46E5, #7C3AED)',
              px: 4,
              py: 2,
              borderRadius: '12px',
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(45deg, #7C3AED, #4F46E5)',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Create Your Card Now
          </Button>
        </Box>
      </Container>

      <style jsx global>{`
        .scroll-reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease-out;
        }
        
        .scroll-reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </Box>
  );
};

export default LandingPage; 